// Precificacao e agregacao de um portfolio de NDF.
//
// A diferenca conceitual para o portfolio de ETF nao e cosmetica: um NDF nao
// tem "ultimo preco". O preco de um contrato e um ponto de uma CURVA forward
// interpolado no vencimento dele — dois contratos do mesmo par com vencimentos
// diferentes valem coisas diferentes no mesmo instante. Por isso o feed aqui e
// indexado por PAR (uma curva) e nao por simbolo (um preco).
//
// Enquanto o engine do Nexus nao esta plugado, a curva e simulada — mas com a
// mesma forma que o engine devolve (spot + nos por prazo, com bid/ask), para
// que a troca seja so a origem do objeto.

import { isMaturityHoliday, parseISODate } from "@/utils/utils";

// Nos da curva em dias uteis: 1d, 1m, 2m, 3m, 6m, 9m, 1a, 18m, 2a.
export const CURVE_NODES_DU = [1, 21, 42, 63, 126, 189, 252, 378, 504];

// spot: nivel inicial. carry: diferencial de juros anual (base 252) que da a
// inclinacao da curva. spreadPips: meia-largura base do bid/ask no vertice
// curto — abre com o prazo. decimals: casas da cotacao; o "ponto" (pip) do par
// e a ultima casa.
const PAIRS = {
  USDBRL: { spot: 5.418, carry: 0.058, spreadPips: 40, decimals: 4 },
  EURBRL: { spot: 5.893, carry: 0.062, spreadPips: 60, decimals: 4 },
  GBPBRL: { spot: 6.845, carry: 0.055, spreadPips: 80, decimals: 4 },
  // O pip do JPYBRL e 1e-6 (6 casas): a largura em pips nao e comparavel a dos
  // pares de 4 casas, so a largura relativa e.
  JPYBRL: { spot: 0.03612, carry: 0.095, spreadPips: 25, decimals: 6 },
};

export const KNOWN_PAIRS = Object.keys(PAIRS);

export const pairConfig = (pair) => PAIRS[pair] || PAIRS.USDBRL;

export const pipSize = (pair) => Math.pow(10, -pairConfig(pair).decimals);

// Volatilidade por tick do feed simulado (0.15%).
const TICK_VOL = 0.0015;

/* ------------------------------------------------------------------ *
 * Calendario
 * ------------------------------------------------------------------ */

const toDate = (value) => {
  if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return parseISODate(value.slice(0, 10));
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isoOf = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// A contagem de dias uteis roda a cada tick do feed, para cada linha. Sem memo
// um portfolio de 2 anos custa ~500 iteracoes por linha a cada 5 segundos.
const duMemo = new Map();

export function businessDaysBetween(from, to) {
  const start = toDate(from);
  const end = toDate(to);
  if (end <= start) return 0;

  const key = `${isoOf(start)}|${isoOf(end)}`;
  if (duMemo.has(key)) return duMemo.get(key);

  let count = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    if (!isMaturityHoliday(cursor)) count += 1;
  }
  duMemo.set(key, count);
  return count;
}

export function calendarDaysBetween(from, to) {
  const ms = toDate(to).getTime() - toDate(from).getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

// Fixing de NDF neste repo e D-1 util do vencimento (mesma convencao do
// Plexus, em components/plexus/components/FxNdf.vue).
export function fixingDateOf(maturity) {
  const d = toDate(maturity);
  do {
    d.setDate(d.getDate() - 1);
  } while (isMaturityHoliday(d));
  return isoOf(d);
}

/* ------------------------------------------------------------------ *
 * Curva
 * ------------------------------------------------------------------ */

// Ruido por vertice, sorteado uma vez na construcao: e o que da FORMATO a
// curva (barriga, ponta torta). O tick move o spot, nao o formato.
function buildShape(nodes) {
  return nodes.map(() => 1 + (Math.random() - 0.5) * 0.0025);
}

export function buildCurve(pair) {
  const cfg = pairConfig(pair);
  return {
    pair,
    spot: cfg.spot,
    carry: cfg.carry,
    shape: buildShape(CURVE_NODES_DU),
    timestamp: Date.now(),
  };
}

export function buildCurves(pairs = KNOWN_PAIRS) {
  const out = {};
  pairs.forEach((p) => {
    out[p] = buildCurve(p);
  });
  return out;
}

// Um tick move o spot de cada par; a curva inteira acompanha por carrego.
// Devolve objeto novo — o Vue 2 nao ve mutacao em chave existente de outro jeito.
export function tickCurves(curves) {
  const next = {};
  Object.keys(curves || {}).forEach((pair) => {
    const cur = curves[pair];
    const drift = (Math.random() - 0.5) * 2 * TICK_VOL;
    next[pair] = {
      ...cur,
      spot: Math.max(0.000001, cur.spot * (1 + drift)),
      timestamp: Date.now(),
    };
  });
  return next;
}

const forwardMid = (curve, du) => curve.spot * Math.pow(1 + curve.carry, du / 252);

// Preco forward do par no prazo `du`, interpolado linearmente entre vertices.
// Fora do ultimo vertice segue por carrego (nao trava no ultimo no) — um
// contrato de 3 anos nao pode valer o mesmo que um de 2.
export function curveAt(curve, du) {
  if (!curve) return { bid: 0, ask: 0, mid: 0 };
  const days = Math.max(0, du);
  const cfg = pairConfig(curve.pair);
  const pip = pipSize(curve.pair);

  let mid;
  const nodes = CURVE_NODES_DU;
  if (days <= nodes[0]) {
    mid = forwardMid(curve, days) * curve.shape[0];
  } else if (days >= nodes[nodes.length - 1]) {
    mid = forwardMid(curve, days) * curve.shape[nodes.length - 1];
  } else {
    let i = 1;
    while (i < nodes.length && nodes[i] < days) i += 1;
    const d0 = nodes[i - 1];
    const d1 = nodes[i];
    const w = (days - d0) / (d1 - d0);
    const m0 = forwardMid(curve, d0) * curve.shape[i - 1];
    const m1 = forwardMid(curve, d1) * curve.shape[i];
    mid = m0 + (m1 - m0) * w;
  }

  const half = (pip * cfg.spreadPips * (0.6 + (days / 252) * 0.8)) / 2;
  return { mid, bid: mid - half, ask: mid + half };
}

/* ------------------------------------------------------------------ *
 * Precificacao do contrato
 * ------------------------------------------------------------------ */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export const isBuySide = (side) => String(side || "BUY").toUpperCase() !== "SELL";

// Lado de FECHAMENTO: quem esta comprado fecha vendendo (bate no bid); quem
// esta vendido fecha comprando (bate no ask). E por isso que o Nexus Price da
// linha depende da ponta da operacao, e nao so do par e do vencimento.
export function nexusSideOf(quote, side) {
  return isBuySide(side) ? quote.bid : quote.ask;
}

// MTM nominal (sem desconto a valor presente — decisao explicita desta fase).
export function priceNdfRow(row, curves, asOf = new Date(), openCurves = null) {
  const pair = row.pair || "USDBRL";
  const curve = (curves || {})[pair];
  const buy = isBuySide(row.side);
  const sign = buy ? 1 : -1;

  const notional = num(row.notional);
  const strike = num(row.strike);
  const du = businessDaysBetween(asOf, row.maturity);
  const dc = calendarDaysBetween(asOf, row.maturity);

  const quote = curveAt(curve, du);
  const nexus = nexusSideOf(quote, row.side);
  const mtm = (nexus - strike) * notional * sign;

  const openQuote = openCurves ? curveAt(openCurves[pair], du) : null;
  const openMtm = openQuote ? (nexusSideOf(openQuote, row.side) - strike) * notional * sign : mtm;

  return {
    ...row,
    pair,
    side: buy ? "BUY" : "SELL",
    sign,
    notional,
    strike,
    du,
    dc,
    expired: du <= 0,
    fixingDate: row.fixingDate || fixingDateOf(row.maturity),
    bid: quote.bid,
    ask: quote.ask,
    mid: quote.mid,
    nexus,
    // Diferenca contra a taxa contratada, em pontos, com o sinal ja na visao
    // do detentor: positivo = a favor.
    points: ((nexus - strike) / pipSize(pair)) * sign,
    mtm,
    dayChange: mtm - openMtm,
    notionalBRL: notional * quote.mid,
    // Exposicao direcional em BRL: e o que soma para o notional liquido.
    signedNotionalBRL: notional * quote.mid * sign,
    timestamp: curve ? curve.timestamp : Date.now(),
  };
}

export function priceNdfRows(rows = [], curves = {}, asOf = new Date(), openCurves = null) {
  const priced = rows.map((r) => priceNdfRow(r, curves, asOf, openCurves));
  const grossBRL = priced.reduce((s, r) => s + Math.abs(r.notionalBRL), 0);
  return priced.map((r) => ({
    ...r,
    weightPct: grossBRL ? (Math.abs(r.notionalBRL) / grossBRL) * 100 : 0,
  }));
}

/* ------------------------------------------------------------------ *
 * Agregacoes
 * ------------------------------------------------------------------ */

// Escada de vencimentos. Cortes em dias CORRIDOS — e assim que a mesa fala de
// vencimento ("o que vence em 30 dias"), diferente dos buckets de tenor do
// FlowSight, que sao rotulos de RFQ.
export const MATURITY_BUCKETS = [
  { key: "d7", maxDc: 7 },
  { key: "d30", maxDc: 30 },
  { key: "d60", maxDc: 60 },
  { key: "d90", maxDc: 90 },
  { key: "d180", maxDc: 180 },
  { key: "d360", maxDc: 360 },
  { key: "d360plus", maxDc: Infinity },
];

export function bucketOf(dc) {
  return MATURITY_BUCKETS.find((b) => dc <= b.maxDc) || MATURITY_BUCKETS[MATURITY_BUCKETS.length - 1];
}

export function ladderByMaturity(rows = []) {
  const acc = new Map(MATURITY_BUCKETS.map((b) => [b.key, { key: b.key, net: 0, gross: 0, mtm: 0, count: 0 }]));
  rows.forEach((r) => {
    const slot = acc.get(bucketOf(r.dc).key);
    slot.net += r.signedNotionalBRL;
    slot.gross += Math.abs(r.notionalBRL);
    slot.mtm += r.mtm;
    slot.count += 1;
  });
  return [...acc.values()];
}

export function exposureByPair(rows = []) {
  const acc = new Map();
  rows.forEach((r) => {
    if (!acc.has(r.pair)) acc.set(r.pair, { pair: r.pair, net: 0, gross: 0, mtm: 0, count: 0, netNotional: 0 });
    const slot = acc.get(r.pair);
    slot.net += r.signedNotionalBRL;
    slot.gross += Math.abs(r.notionalBRL);
    slot.netNotional += r.notional * r.sign;
    slot.mtm += r.mtm;
    slot.count += 1;
  });
  return [...acc.values()].sort((a, b) => b.gross - a.gross);
}

export function ndfTotals(rows = []) {
  const totals = {
    netNotionalBRL: 0,
    grossNotionalBRL: 0,
    mtm: 0,
    dayChange: 0,
    count: rows.length,
    maturingD7BRL: 0,
    nextMaturity: null,
    fixingsToday: 0,
  };
  const todayIso = isoOf(new Date());

  rows.forEach((r) => {
    totals.netNotionalBRL += r.signedNotionalBRL;
    totals.grossNotionalBRL += Math.abs(r.notionalBRL);
    totals.mtm += r.mtm;
    totals.dayChange += r.dayChange;
    if (r.dc <= 7) totals.maturingD7BRL += Math.abs(r.notionalBRL);
    if (r.fixingDate === todayIso) totals.fixingsToday += 1;
    if (!totals.nextMaturity || r.maturity < totals.nextMaturity) totals.nextMaturity = r.maturity;
  });

  return totals;
}

// Prazo medio ponderado pelo notional, em dias corridos.
export function weightedAverageMaturity(rows = []) {
  let w = 0;
  let acc = 0;
  rows.forEach((r) => {
    const abs = Math.abs(r.notionalBRL);
    w += abs;
    acc += abs * r.dc;
  });
  return w ? acc / w : 0;
}
